import { useMemo, useState } from 'react';

import { cn } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/button';
import { GROUP_LABEL } from '../../shared/ui/group-label';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  FolderIcon,
  GrowthIcon,
  SearchIcon,
} from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import {
  OLDER_DATA,
  PeriodPicker,
  folderSourceWords,
  formatBytes,
  type TFolderLevel,
  type TRetentionPeriod,
  type TStorageLevels,
} from '../../entities/retention';
import { ImpactDialog } from './impact-dialog';

const FEW = 7;
const SHOWN = 6;

const COL = {
  keeps: 'w-col-keeps shrink-0',
  flows: 'w-col-flows-n shrink-0 text-right font-mono text-meta',
  size: 'w-col-size shrink-0 text-right font-mono text-meta',
  growth: 'w-col-growth shrink-0 text-right font-mono text-meta text-muted',
  note: 'min-w-0 flex-1',
} as const;

const ROW = 'flex min-h-row-xl items-center gap-3 border-t border-border-row px-card';

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const Note = ({ row }: { row: Pick<TFolderLevel, 'fastGrowing' | 'shortOfRetries'> }) => {
  if (row.shortOfRetries > 0) {
    return (
      <span className="flex items-center gap-label text-meta text-motion">
        <ClockIcon size="sm" />
        {plural(row.shortOfRetries, 'flow is', 'flows are')} shorter than{' '}
        {row.shortOfRetries === 1 ? 'its' : 'their'} retries
      </span>
    );
  }
  if (row.fastGrowing > 0) {
    return (
      <span className="flex items-center gap-label text-meta text-motion">
        <GrowthIcon size="sm" />
        {plural(row.fastGrowing, 'flow is', 'flows are')} growing fast
      </span>
    );
  }
  return null;
};

interface IChange {
  folder: TFolderLevel;
  period: TRetentionPeriod | null;
}

export const FolderTable = ({ levels }: { levels: TStorageLevels }) => {
  const [open, setOpen] = useState<Set<string>>(() => {
    const first = levels.folders.find((folder) => folder.parentId === null);
    const hasKids = first && levels.folders.some((folder) => folder.parentId === first.folderId);
    return new Set(hasKids && first ? [first.folderId] : []);
  });
  const [all, setAll] = useState(false);
  const [q, setQ] = useState('');
  const [change, setChange] = useState<IChange | null>(null);

  const byId = useMemo(
    () => new Map(levels.folders.map((folder) => [folder.folderId, folder])),
    [levels.folders],
  );
  const kids = (id: string | null) => levels.folders.filter((folder) => folder.parentId === id);
  const top = kids(null);
  const many = levels.folders.length > FEW;
  const needle = q.trim().toLowerCase();

  // Depth-first, children under their parent, each level biggest first (the list's order).
  const visible: TFolderLevel[] = [];
  if (needle) {
    visible.push(...levels.folders.filter((folder) => folder.name.toLowerCase().includes(needle)));
  } else {
    const walk = (folder: TFolderLevel) => {
      visible.push(folder);
      if (open.has(folder.folderId)) kids(folder.folderId).forEach(walk);
    };
    (many && !all ? top.slice(0, SHOWN) : top).forEach(walk);
  }
  const hidden = many && !all && !needle ? top.slice(SHOWN) : [];

  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const parentOf = (folder: TFolderLevel) =>
    folder.parentId ? byId.get(folder.parentId) : undefined;

  return (
    <div>
      <div className="flex items-center gap-inset px-card pt-4 pb-inset">
        <h3 className="text-data font-semibold text-primary">Folders</h3>
        <span className="font-mono text-micro text-faint">
          {many
            ? 'biggest first'
            : `${plural(levels.folders.length, 'folder')} · ${plural(levels.flowCount, 'flow')}`}
        </span>
        {many && (
          <Input
            size="sm"
            type="search"
            aria-label="Find a folder"
            placeholder="Find a folder"
            leading={<SearchIcon size="sm" />}
            value={q}
            onChange={(event) => setQ(event.target.value)}
            className="ml-auto w-col-keeps"
          />
        )}
      </div>
      <div
        className={cn(
          'flex h-row items-center gap-3 border-b border-border-sunken px-card',
          GROUP_LABEL,
        )}
      >
        <span className="min-w-0 flex-1">Folder</span>
        <span className={COL.keeps}>Keeps</span>
        <span className="w-col-flows-n shrink-0 text-right">Flows</span>
        <span className="w-col-size shrink-0 text-right">Stored</span>
        <span className="w-col-growth shrink-0 text-right">Last 30 days</span>
        <span className={COL.note} />
      </div>
      {visible.map((folder) => {
        const parent = parentOf(folder);
        const expandable = kids(folder.folderId).length > 0 && !needle;
        return (
          <div key={folder.folderId} className={ROW}>
            <span className="flex min-w-0 flex-1 items-center gap-2 text-data text-primary">
              {!needle &&
                Array.from({ length: folder.depth - 1 }, (_, i) => (
                  <span key={i} aria-hidden className="w-5 shrink-0" />
                ))}
              <button
                type="button"
                aria-label={open.has(folder.folderId) ? 'Collapse' : 'Expand'}
                disabled={!expandable}
                onClick={() => toggle(folder.folderId)}
                className={cn(
                  'flex w-4 shrink-0 justify-center text-muted',
                  !expandable && 'invisible',
                )}
              >
                {open.has(folder.folderId) ? (
                  <ChevronDownIcon size="sm" />
                ) : (
                  <ChevronRightIcon size="sm" />
                )}
              </button>
              <FolderIcon size="sm" className="text-muted" />
              <span className="truncate">{folder.name}</span>
            </span>
            <span className={COL.keeps}>
              <PeriodPicker
                aria-label={`How long ${folder.name} keeps things`}
                value={folder.keeps.period}
                own={folder.ownPeriod}
                source={folderSourceWords(folder.keeps, folder.ownPeriod)}
                follow={{
                  label: parent ? `Follow ${parent.name}` : 'Follow the default',
                  period: parent ? parent.keeps.period : levels.defaultPeriod,
                }}
                onChoose={(period) => setChange({ folder, period })}
              />
            </span>
            <span className={COL.flows}>{folder.flows.toLocaleString('en-US')}</span>
            <span className={COL.size}>{formatBytes(folder.bytes)}</span>
            <span className={COL.growth}>+{formatBytes(folder.growth30dBytes)}</span>
            <span className={COL.note}>
              <Note row={folder} />
            </span>
          </div>
        );
      })}
      {!needle && (
        <div className={ROW}>
          <span className="flex min-w-0 flex-1 items-center gap-2 text-data text-muted">
            <span aria-hidden className="w-6 shrink-0" />
            Not in a folder
          </span>
          <span className={COL.keeps}>
            <PeriodPicker
              aria-label="How long flows in no folder keep things"
              value={levels.unfiled.keeps.period}
              own={null}
              source="default"
              disabled
              onChoose={() => undefined}
            />
          </span>
          <span className={COL.flows}>{levels.unfiled.flows.toLocaleString('en-US')}</span>
          <span className={COL.size}>{formatBytes(levels.unfiled.bytes)}</span>
          <span className={COL.growth}>+{formatBytes(levels.unfiled.growth30dBytes)}</span>
          <span className={COL.note}>
            <Note row={levels.unfiled} />
          </span>
        </div>
      )}
      {!needle && levels.older.bytes > 0 && (
        <div className={ROW}>
          <span className="flex min-w-0 flex-1 items-center gap-2 text-data text-muted">
            <span aria-hidden className="w-6 shrink-0" />
            {OLDER_DATA}
          </span>
          <span className={COL.keeps}>
            <PeriodPicker
              aria-label="How long older data is kept"
              value={levels.older.keeps.period}
              own={null}
              source="default"
              disabled
              onChoose={() => undefined}
            />
          </span>
          <span className={COL.flows}>—</span>
          <span className={COL.size}>{formatBytes(levels.older.bytes)}</span>
          <span className={COL.growth}>+{formatBytes(levels.older.growth30dBytes)}</span>
          <span className={cn(COL.note, 'text-meta text-muted')}>
            no flow owns it · follows the default
          </span>
        </div>
      )}
      {!needle && levels.keptBytes > 0 && (
        <div className={ROW}>
          <span className="flex min-w-0 flex-1 items-center gap-2 text-data text-muted">
            <span aria-hidden className="w-6 shrink-0" />
            Issues and bookmarks
          </span>
          <span className={cn(COL.keeps, 'text-meta text-muted')}>until you delete them</span>
          <span className={COL.flows}>—</span>
          <span className={COL.size}>{formatBytes(levels.keptBytes)}</span>
          <span className={COL.growth} />
          <span className={COL.note} />
        </div>
      )}
      {needle && visible.length === 0 && (
        <p className="border-t border-border-row px-card py-3 text-meta text-muted">
          No folder is called anything like “{q.trim()}”.
        </p>
      )}
      {hidden.length > 0 && (
        <div className="flex items-center gap-inset border-t border-border-row px-card py-3">
          <span className="font-mono text-micro text-faint">
            {SHOWN} of {plural(top.length, 'top folder')} · the other {hidden.length} hold{' '}
            {formatBytes(hidden.reduce((sum, folder) => sum + folder.bytes, 0))} in{' '}
            {plural(
              hidden.reduce((sum, folder) => sum + folder.flows, 0),
              'flow',
            )}{' '}
            · {plural(levels.unfiled.flows, 'flow is', 'flows are')} not in a folder
          </span>
          <Button variant="ghost" size="xs" className="ml-auto" onClick={() => setAll(true)}>
            Show all {top.length}
          </Button>
        </div>
      )}
      {change && (
        <ImpactDialog
          target={{
            level: 'folder',
            id: change.folder.folderId,
            name: change.folder.name,
            parentName: parentOf(change.folder)?.name ?? null,
          }}
          period={change.period}
          from={change.folder.keeps.period}
          onClose={() => setChange(null)}
        />
      )}
    </div>
  );
};
